import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const VAT_RATE = 0.06;

type CustomerLite = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
};

interface Line {
  description: string;
  quantity: string;
  unitPrice: string; // kronor, exkl. moms
}

const emptyLine = (): Line => ({ description: "", quantity: "1", unitPrice: "" });

const labelFor = (c: CustomerLite) =>
  c.company || [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email;

const formatSEK = (cents: number) =>
  new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK", maximumFractionDigits: 2 }).format(
    cents / 100,
  );

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId?: string;
  customerName?: string;
  /** When set, the dialog is pre-filled with the lines of this invoice. */
  sourceInvoiceId?: string;
  sourceInvoiceNumber?: string | null;
  onCreated?: () => void;
}

const CreateInvoiceSheet = ({
  open,
  onOpenChange,
  userId,
  customerName,
  sourceInvoiceId,
  sourceInvoiceNumber,
  onCreated,
}: Props) => {
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string>(userId ?? "");
  const [lines, setLines] = useState<Line[]>([emptyLine()]);
  const [daysUntilDue, setDaysUntilDue] = useState("30");
  const [busy, setBusy] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);

  useEffect(() => {
    setSelectedUserId(userId ?? "");
  }, [userId, open]);

  // Pre-fill from an existing invoice (copy / replace flow)
  useEffect(() => {
    if (!open || !sourceInvoiceId) return;
    let cancelled = false;
    setLoadingSource(true);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("admin-get-invoice-lines", {
          body: { invoice_id: sourceInvoiceId },
        });
        if (error) throw error;
        if ((data as any)?.error) throw new Error((data as any).error);
        if (cancelled) return;
        const src = data as {
          user_id: string;
          days_until_due: number;
          lines: Array<{ description: string; quantity: number; unit_amount_cents: number }>;
        };
        if (src.user_id) setSelectedUserId(src.user_id);
        setDaysUntilDue(String(src.days_until_due ?? 30));
        setLines(
          src.lines?.length
            ? src.lines.map((l) => ({
                description: l.description,
                quantity: String(l.quantity),
                unitPrice: String(l.unit_amount_cents / 100),
              }))
            : [emptyLine()],
        );
        if (!src.lines?.length) {
          toast.warning("Hittade inga rader på den gamla fakturan – fyll i manuellt");
        }
      } catch (e: any) {
        if (!cancelled) toast.error(e?.message || "Kunde inte hämta den gamla fakturan");
      } finally {
        if (!cancelled) setLoadingSource(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sourceInvoiceId]);

  useEffect(() => {
    if (!open || userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, email, first_name, last_name, company")
        .order("created_at", { ascending: false })
        .limit(500);
      if (!cancelled) setCustomers((data as CustomerLite[]) || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 30);
    return customers
      .filter((c) =>
        `${c.first_name ?? ""} ${c.last_name ?? ""} ${c.company ?? ""} ${c.email}`.toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [customers, customerQuery]);

  const subtotalCents = lines.reduce((sum, l) => {
    const qty = Number(l.quantity.replace(",", ".")) || 0;
    const price = Number(l.unitPrice.replace(",", ".")) || 0;
    return sum + Math.round(qty * price * 100);
  }, 0);
  const vatCents = Math.round(subtotalCents * VAT_RATE);

  const updateLine = (i: number, patch: Partial<Line>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const reset = () => {
    setLines([emptyLine()]);
    setDaysUntilDue("30");
    setCustomerQuery("");
    setSelectedUserId(userId ?? "");
  };

  const submit = async (send: boolean) => {
    const targetUser = userId ?? selectedUserId;
    if (!targetUser) {
      toast.error("Välj en kund");
      return;
    }
    const payloadLines = lines
      .map((l) => ({
        description: l.description.trim(),
        quantity: Math.max(1, Math.floor(Number(l.quantity.replace(",", ".")) || 0)),
        unit_amount_cents: Math.round((Number(l.unitPrice.replace(",", ".")) || 0) * 100),
      }))
      .filter((l) => l.description && l.unit_amount_cents > 0);

    if (payloadLines.length === 0) {
      toast.error("Lägg till minst en rad med beskrivning och pris");
      return;
    }

    const days = Math.max(1, Math.min(120, Math.floor(Number(daysUntilDue) || 30)));

    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-create-and-send-invoice", {
        body: {
          user_id: targetUser,
          lines: payloadLines,
          send,
          days_until_due: days,
          source_invoice_id: sourceInvoiceId ?? null,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(send ? "Faktura skapad och skickad" : "Fakturautkast skapat");
      onOpenChange(false);
      reset();
      onCreated?.();
    } catch (e: any) {
      toast.error(e?.message || "Kunde inte skapa fakturan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{sourceInvoiceId ? "Ny faktura från en gammal" : "Ny faktura"}</DialogTitle>
          <DialogDescription>
            {sourceInvoiceId
              ? `${loadingSource ? "Hämtar raderna" : "Raderna är hämtade"} från faktura ${
                  sourceInvoiceNumber ?? ""
                }. Ändra det du vill – den nya fakturan får nytt fakturanummer, nytt OCR och bankgiro.`
              : customerName
                ? `Skapa en faktura åt ${customerName}. OCR-nummer och bankgiro läggs till automatiskt.`
                : "Skapa en faktura direkt hos betalleverantören. OCR-nummer, bankgiro och referens läggs till automatiskt."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!userId && (
            <div>
              <Label>Kund</Label>
              <Input
                className="mb-2"
                placeholder="Sök på namn, företag eller e-post"
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
              />
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Välj kund" />
                </SelectTrigger>
                <SelectContent>
                  {filteredCustomers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {labelFor(c)} — {c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Fakturarader (pris exkl. moms)</Label>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_100px_36px] gap-2 items-center">
                <Input
                  placeholder="Beskrivning"
                  value={l.description}
                  onChange={(e) => updateLine(i, { description: e.target.value })}
                />
                <Input
                  inputMode="numeric"
                  placeholder="Antal"
                  value={l.quantity}
                  onChange={(e) => updateLine(i, { quantity: e.target.value })}
                />
                <Input
                  inputMode="decimal"
                  placeholder="kr/st"
                  value={l.unitPrice}
                  onChange={(e) => updateLine(i, { unitPrice: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={lines.length === 1}
                  onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
            >
              <Plus className="h-3.5 w-3.5 mr-2" />
              Lägg till rad
            </Button>
          </div>

          <div className="rounded-lg border p-3 bg-muted/20 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delsumma</span>
              <span>{formatSEK(subtotalCents)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Moms 6 %</span>
              <span>{formatSEK(vatCents)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>Totalt</span>
              <span>{formatSEK(subtotalCents + vatCents)}</span>
            </div>
          </div>

          <div className="w-40">
            <Label>Förfaller om (dagar)</Label>
            <Input
              inputMode="numeric"
              value={daysUntilDue}
              onChange={(e) => setDaysUntilDue(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Avbryt
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => submit(false)}>
            Spara som utkast
          </Button>
          <Button disabled={busy} onClick={() => submit(true)}>
            {busy ? "Arbetar…" : "Skapa och skicka"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInvoiceSheet;
